import type { DashboardFilter } from '../../../types/insights'

/** Every widget's shared contract — `DashboardScreen` wires these identically for all six tiles. */
export interface WidgetProps {
  filter: DashboardFilter
  /** AC10 — true on the render where this widget itself was the cross-filter's source; it skips its own re-fetch. */
  skipRefetch: boolean
  onCrossFilter: (axis: keyof DashboardFilter, value: string) => void
  /** AC12 — navigates to the drill target pre-filtered to an RSQL clause. */
  onDrill: (rsql: string) => void
}

export const WIDGET_KEY = {
  filingsByStatus: 'filings-by-status',
  creditValueByJurisdictionFy: 'credit-value-by-jurisdiction-fy',
  outcomeVariance: 'outcome-variance',
  qreSpendTrend: 'qre-spend-trend',
  monetizationOutcomes: 'monetization-outcomes',
  readiness: 'readiness',
  /**
   * Client-only identifiers — NOT a real `/api/v1/insights/widgets/{widgetKey}` endpoint. Both
   * tiles are frontend-only compositions over the six real widget keys above (each re-fetched
   * independently via its own real key); these two exist only so `DashboardScreen`'s
   * `widgetProps(widgetKey)` has a stable identity for cross-filter-origin bookkeeping, same as
   * every other tile.
   */
  portfolioPipelineFunnel: 'portfolio-pipeline-funnel',
  creditsByJurisdictionStatus: 'credits-by-jurisdiction-status',
  /** IB-282 View 3, AC3.2 — real `/insights/widgets/program-rate-comparison` endpoint. */
  programRateComparison: 'program-rate-comparison',
  /** IB-282 View 3, AC3.3 — real `/insights/widgets/allocation-pool-utilization` endpoint. */
  allocationPoolUtilization: 'allocation-pool-utilization',
  /** IB-282 View 1, AC1.3 — real `/insights/widgets/certified-value-by-fiscal-year` endpoint. */
  certifiedValueByFiscalYear: 'certified-value-by-fiscal-year',
  /** IB-282 View 1, AC1.6 — real `/insights/widgets/certified-value-by-account` endpoint. */
  certifiedValueByAccount: 'certified-value-by-account',
  /** IB-282 View 4, AC4.1 — real `/insights/widgets/case-aging-by-status` endpoint. */
  caseAgingByStatus: 'case-aging-by-status',
  /**
   * IB-282 View 4, AC4.2 — manifest-gate-only (design §3.1): NOT a real
   * `/insights/widgets/{key}` endpoint. Data is fetched client-side from `GET /ingestion-runs`; this
   * key exists only for `useWidgetData`-style cross-filter-origin bookkeeping and the manifest's
   * capability render-gate lookup.
   */
  ingestionRunVolume: 'ingestion-run-volume',
  /** IB-282 View 4, AC4.2 — manifest-gate-only, same posture as `ingestionRunVolume` above; sourced from `GET /ai-reviews`. */
  aiAcceptRate: 'ai-accept-rate',
} as const
